import {Routes} from '@angular/router';
import {LoginComponent} from '@app/authentication/login/login.component';
import {LogoutComponent} from '@app/authentication/logout/logout.component';
import {PDEGuestComponent} from '@app/authentication/pde-guest/pde-guest.component';
import {SessionComponent} from '@app/authentication/session/session.component';
import {VpnDisconnectedComponent} from '@app/authentication/vpn-disconnected/vpn-disconnected.component';
import pageMeta from '@app/data/page-meta.json';
import {DatasetAccessComponent} from '@app/dataset/dataset-access/dataset-access.component';
import {PageNotFoundComponent} from '@app/navigation/page-not-found/page-not-found.component';
import {PrivateDataEnclaveComponent} from '@app/private-data-enclave/private-data-enclave.component';
import {PublicDataCommonsComponent} from '@app/public-data-commons/public-data-commons.component';
import {ResourceAddComponent} from '@app/resource-explorer/resource-add.component';
import {ResourceExplorerComponent} from '@app/resource-explorer/resource-explorer.component';
import {ResourceComponent} from '@app/resource/resource.component';
import {HelpComponent} from '@help/help.component';
import {HomeComponent} from '@home/home.component';
import {PrivateProjectComponent} from '@project/private-project/private-project.component';
import {ProjectAccessComponent} from '@project/project-access/project-access.component';
import {ProjectAddComponent} from '@project/project-add/project-add.component';
import {ProjectDetailsComponent} from '@project/project-details/project-details.component';
import {PublicProjectComponent} from '@project/public-project/public-project.component';
import {authenticatedUserGuard} from '@shared/guards/authenticated-user.guard';
import {moduleEnabledGuard} from '@shared/guards/module-enabled.guard';
import {privateDataEnclaveAuthGuard} from '@shared/guards/private-data-enclave-auth.guard';
import {privateDataEnclaveEulaGuard} from '@shared/guards/private-data-enclave-eula.guard';
import {privateDataEnclaveOrgGuard} from '@shared/guards/private-data-enclave-org.guard';
import {privateDataEnclaveVpnGuard} from '@shared/guards/private-data-enclave-vpn.guard';
import {ResourceEditorGuard} from '@shared/guards/resource-editor.guard';
import {unsavedDataGuard} from '@shared/guards/unsaved-data.guard';
import {homeContentResolver} from '@shared/resolvers/home-content.resolver';
import {privateDatasetResolver} from '@shared/resolvers/private-dataset.resolver';
import {privateProjectResolver} from '@shared/resolvers/private-project.resolver';
import {projectAddResolver} from '@shared/resolvers/project-add.resolver';
import {publicDatasetResolver} from '@shared/resolvers/public-dataset.resolver';
import {publicProjectResolver} from '@shared/resolvers/public-project.resolver';
import {withFlag} from '@shared/resolvers/resolver-with-flags';
import {resourceResolver} from '@shared/resolvers/resource.resolver';
import {UserResolver} from '@shared/resolvers/user.resolver';
import {validationDataResolver} from '@shared/resolvers/validation-data.resolver';
import {TimedoutComponent} from './authentication/timedout/timedout.component';
import {DatasetAddComponent, DatasetEditComponent} from './dataset/dataset-add-edit/dataset-add-edit';
import {DatasetViewPrivate} from './dataset/dataset-view-private/dataset-view-private';
import {DatasetViewPublic} from './dataset/dataset-view-public/dataset-view-public';
import {ResourceAccessComponent} from './resource-explorer/resource-access.component';
import {ResourceDetailsComponent} from './resource-explorer/resource-details.component';

export const routes: Routes = [
  // Non-user routes
  {path: 'login', component: LoginComponent, data: {pageMeta: pageMeta['login']}},
  {path: 'logout', component: LogoutComponent},
  {path: 'session/:idp', component: SessionComponent},
  {path: 'timedout', component: TimedoutComponent},

  {
    path: '',
    resolve: {user: UserResolver},
    children: [
      {path: '', component: HomeComponent, resolve: {homePageContent: homeContentResolver}},

      {
        path: 'resource-explorer',
        canMatch: [moduleEnabledGuard],
        data: {siteHeaderSection: 'Resource Explorer'},
        children: [
          {path: '', pathMatch: 'full', redirectTo: 'search'},
          {
            path: 'search',
            component: ResourceExplorerComponent,
            data: {pageMeta: pageMeta['resource-explorer/search']},
          },

          {
            path: 'view/:id',
            component: ResourceComponent,
            resolve: {resource: resourceResolver},
          },
          {
            path: 'add',
            component: ResourceAddComponent,
            canActivate: [authenticatedUserGuard],
            canDeactivate: [unsavedDataGuard],
          },
          {
            path: 'edit-details/:id',
            component: ResourceDetailsComponent,
            canActivate: [ResourceEditorGuard],
            canDeactivate: [unsavedDataGuard],
            resolve: {resource: resourceResolver},
          },
          {
            path: 'edit-access/:id',
            component: ResourceAccessComponent,
            canActivate: [ResourceEditorGuard],
            canDeactivate: [unsavedDataGuard],
            resolve: {resource: resourceResolver},
          },
        ],
      },

      {
        path: 'public-data-commons',
        canMatch: [moduleEnabledGuard],
        data: {siteHeaderSection: 'Public Data Commons'},
        children: [
          {path: '', pathMatch: 'full', redirectTo: 'search'},
          {
            path: 'search',
            component: PublicDataCommonsComponent,
            data: {pageMeta: pageMeta['public-data-commons/search']},
          },

          {
            path: 'project',
            children: [
              {
                path: 'view/:projectId',
                component: PublicProjectComponent,
                resolve: {projectData: publicProjectResolver},
              },
            ],
          },

          {
            path: 'dataset',
            children: [
              {
                path: 'view/:datasetId',
                component: DatasetViewPublic,
                resolve: {dataset: publicDatasetResolver},
              },
            ],
          },
        ],
      },

      {
        path: 'private-data-enclave',
        canMatch: [moduleEnabledGuard],
        data: {
          siteHeaderSection: 'Private Data Enclave',
          siteHeaderShield: true,
          pageMeta: pageMeta['private-data-enclave/search'],
        },
        canActivateChild: [
          privateDataEnclaveAuthGuard,
          privateDataEnclaveOrgGuard,
          privateDataEnclaveVpnGuard,
          privateDataEnclaveEulaGuard,
        ],

        children: [
          {path: '', pathMatch: 'full', redirectTo: 'search'},

          {
            path: 'search',
            component: PrivateDataEnclaveComponent,
            data: {pageMeta: pageMeta['private-data-enclave/search']},
          },

          {
            path: 'project',
            children: [
              {
                path: 'view/:projectId',
                component: PrivateProjectComponent,
                resolve: {projectData: privateProjectResolver},
              },
              {
                path: 'add',
                component: ProjectAddComponent,
                canActivate: [authenticatedUserGuard],
                canDeactivate: [unsavedDataGuard],
                resolve: {
                  validationData: validationDataResolver,
                  projectAddData: projectAddResolver,
                },
              },
              {
                path: 'edit-details/:projectId',
                component: ProjectDetailsComponent,
                canActivate: [authenticatedUserGuard],
                canDeactivate: [unsavedDataGuard],
                resolve: {
                  validationData: validationDataResolver,
                  projectData: withFlag(privateProjectResolver, data => data.project.flags._can_update_meta),
                },
              },
              {
                path: 'edit-access/:projectId',
                component: ProjectAccessComponent,
                resolve: {
                  projectData: withFlag(privateProjectResolver, data => data.project.flags._can_manage_permission),
                },
              },
            ],
          },

          {
            path: 'dataset',
            children: [
              {
                path: 'view/:datasetId',
                component: DatasetViewPrivate,
                resolve: {dataset: privateDatasetResolver},
              },
              {
                path: 'add',
                component: DatasetAddComponent,
                canActivate: [authenticatedUserGuard],
                canDeactivate: [unsavedDataGuard],
              },
              {
                path: 'edit-details/:datasetId',
                component: DatasetEditComponent,
                canDeactivate: [unsavedDataGuard],
                resolve: {
                  dataset: withFlag(privateDatasetResolver, data => data._can_update_meta),
                },
              },
              {
                path: 'edit-access/:datasetId',
                component: DatasetAccessComponent,
                resolve: {
                  dataset: withFlag(privateDatasetResolver, data => data._can_manage_permission),
                },
              },
            ],
          },
        ],
      },

      {
        path: 'help',
        children: [
          {path: '', component: HelpComponent, data: {pageMeta: pageMeta['help']}},
          {path: ':section-slug', component: HelpComponent, data: {pageMeta: pageMeta['help']}},
          {path: ':section-slug/:subsection-slug', component: HelpComponent, data: {pageMeta: pageMeta['help']}},
          {
            path: ':section-slug/:subsection-slug/:tertiary-section-slug',
            component: HelpComponent,
            data: {pageMeta: pageMeta['help']},
          },
        ],
      },
    ],
  },

  {path: 'vpn-disconnected', component: VpnDisconnectedComponent},
  {path: 'pde-guest', component: PDEGuestComponent, data: {pageMeta: pageMeta['private-data-enclave/search']}},
  {path: '**', component: PageNotFoundComponent},
];
